/**
 * EvalOverlayControlBar — Slice 1f
 *
 * Unified overlay control bar for the athlete video panel.  Replaces the
 * scattered overlay toggles (master showOverlays button, VisualizationModeSelector,
 * per-panel trail selectors) with one clean pill bar.
 *
 * State model (EvalOverlayState):
 *   pose     — whether the UnifiedPoseOverlay is rendered (any mode != 'off')
 *   ghost    — toggles between 'ghost_echo' mode and 'off' mode (RF#1)
 *   trails   — whether trajectory trail overlays are active
 *   goals    — independent bit for GoalOverlayLayer on BOTH panels (RF#3)
 *   regions  — whether hull/body-region overlays are shown
 *   viewMode — '2d' | '3d' skeleton toggle
 *
 * Ghost maps to setMode('ghost_echo' | 'off') on the existing useVisualizationMode
 * hook — NOT a new canvas.  Goals is an independent bit decoupled from the retired
 * master showOverlays gate.
 */

import React from 'react';
import type { EvalOverlayState } from './evalOverlayState';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EvalOverlayControlBarProps {
  state: EvalOverlayState;
  /** Called when a pill is toggled. Parent should update state and call any
   *  downstream setters (e.g. setMode on the viz hook). */
  onPoseToggle: () => void;
  onGhostToggle: () => void;
  onGoalsToggle: () => void;
  onRegionsToggle: () => void;
  /** Render the Ghost pill. Default true (athlete panel). The TEMPLATE panel
   *  passes false: the template IS the reference, so a "ghost of the reference"
   *  is meaningless there — the pill previously just dimmed the skeleton, which
   *  read as broken (owner bug report 2026-08-01). */
  showGhost?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Pill button primitive
// ---------------------------------------------------------------------------

interface PillProps {
  label: string;
  active: boolean;
  onClick: () => void;
  'data-testid'?: string;
  disabled?: boolean;
}

function Pill({ label, active, onClick, 'data-testid': testId, disabled }: PillProps) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={[
        'px-3 py-1 rounded-[var(--results-radius-pill)] text-[11px] font-medium',
        'border transition-colors duration-150',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
        'select-none',
        active
          ? [
              'border-[color:var(--results-accent-report)]',
              'bg-[color:color-mix(in_srgb,var(--results-accent-report)_15%,transparent)]',
              'text-[color:var(--results-accent-report)]',
            ].join(' ')
          : [
              'border-[color:var(--results-surface-line)]',
              'bg-[color:var(--results-surface-raised-2)]',
              'text-[color:var(--results-text-dim)]',
              'hover:text-[color:var(--results-text-primary)]',
              'hover:border-[color:color-mix(in_srgb,var(--results-text-faint)_80%,transparent)]',
            ].join(' '),
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Divider
// ---------------------------------------------------------------------------

function PillDivider() {
  return (
    <span
      className="w-px h-4 flex-shrink-0 self-center"
      style={{ background: 'var(--results-surface-line)' }}
      aria-hidden="true"
    />
  );
}

// ---------------------------------------------------------------------------
// EvalOverlayControlBar
// ---------------------------------------------------------------------------

export function EvalOverlayControlBar({
  state,
  onPoseToggle,
  onGhostToggle,
  onGoalsToggle,
  onRegionsToggle,
  showGhost = true,
  className,
}: EvalOverlayControlBarProps) {
  return (
    <div
      data-testid="eval-overlay-control-bar"
      className={[
        'flex flex-wrap items-center gap-1.5 px-3 py-2',
        'border-t',
        className ?? '',
      ].join(' ')}
      style={{
        borderColor: 'var(--results-surface-line)',
        background: 'var(--results-surface-raised-1)',
      }}
      aria-label="Overlay controls"
      role="toolbar"
    >
      {/* Pose skeleton overlay */}
      <Pill
        label="Pose"
        active={state.pose}
        onClick={onPoseToggle}
        data-testid="pill-pose"
      />

      {/* Ghost echo — maps to setMode('ghost_echo' | 'off').
          Hidden on the template panel (showGhost=false) — no ghost exists there. */}
      {showGhost && (
        <Pill
          label="Ghost"
          active={state.ghost}
          onClick={onGhostToggle}
          data-testid="pill-ghost"
        />
      )}

      {/* NOTE: Trace Pointes (trajectory trails) is NOT a pill here — it is the
          signature feature and gets its own prominent hero control below the
          video (TracePointesHeroControl). Demoting it to a bar pill buried it
          (owner regression report 2026-07-27). state.trails is still driven by
          that hero control's on/off. */}

      <PillDivider />

      {/* Goal annotation overlays — independent bit, both panels */}
      <Pill
        label="Goals"
        active={state.goals}
        onClick={onGoalsToggle}
        data-testid="pill-goals"
      />

      {/* Body-region hull overlays */}
      <Pill
        label="Regions"
        active={state.regions}
        onClick={onRegionsToggle}
        data-testid="pill-regions"
      />

      {/* NOTE: the 2D/3D mode switch is NOT in this bar. This bar lives inside the
          video-compare grid, which is collapsed (invisible h-0) in 3D mode — so a
          3D toggle here would vanish once engaged, with no way back to 2D (owner
          regression report 2026-07-27). The mode switch is now a persistent,
          report-styled segmented control ABOVE the grid (EvalViewModeSwitch). */}
    </div>
  );
}

export default EvalOverlayControlBar;

// State model, DEFAULT_OVERLAY_STATE, ghostToVizMode, overlayBitsToVizMode,
// togglePoseBit, toggleGhostBit moved to evalOverlayState.ts
// (react-refresh/only-export-components).
